import React from "react";
import { Button } from "@/components/ui/button";
import { ChevronRightIcon, X } from "lucide-react";
import { useFormStore } from "@/contexts/paymentFlowContext";
import { useLocation, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

interface GenericStepHeaderProps {
  steps: string[];
  currentStep: number;
  handleModalClose: () => void;
  setCurrentStep: (step: number) => void;
}

export const GenericStepHeader = ({
  steps,
  currentStep,
  handleModalClose,
  setCurrentStep,
}: GenericStepHeaderProps) => {
  const { t } = useTranslation();
  return (
    <div className="sticky top-0 z-10 flex w-full flex-col items-start justify-between bg-background py-2 md:pb-0">
      <div className="flex w-full items-center justify-between gap-4 bg-background">
        <div className="flex items-center gap-2">
          {steps.map((step, index) => (
            <React.Fragment key={step}>
              <button
                className={`text-sm leading-[140%] ${index + 1 === currentStep ? "font-bold" : "text-foreground/50"}
                        ${index + 1 < currentStep ? "cursor-pointer hover:underline" : ""}`}
                onClick={() => index + 1 < currentStep && setCurrentStep(index + 1)}
              >
                {step}
              </button>
              {index < steps.length - 1 && (
                <ChevronRightIcon
                  className={cn(
                    "rtl:-scale-100",
                    index + 1 < currentStep ? "stroke-foreground" : "stroke-[#695B6A]",
                  )}
                  size={16}
                  strokeWidth={3}
                />
              )}
            </React.Fragment>
          ))}
        </div>
        <Button
          variant={"ghost"}
          aria-label={t("closeModal")}
          className="ring-offset-background hover:bg-transparent focus:outline-none focus:ring-0 focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground"
          onClick={handleModalClose}
        >
          <X className="h-6 w-6 stroke-foreground" />
        </Button>
      </div>
    </div>
  );
};

export default function StepHeader() {
  const { t } = useTranslation();
  const { currentStep, reset, setCurrentStep } = useFormStore();
  const { search } = useLocation();
  const navigate = useNavigate();

  const handleModalClose = () => {
    reset();
    if (search.response_code) {
      navigate({ to: "/" });
    } else {
      // Check if user came from COH landing and redirect appropriately
      if (search.source === "coh") {
        navigate({ to: "/coh" });
      } else {
        navigate({ to: search.backUrl ?? "/" });
      }
    }
  };

  // Get isDonateNowFlow from the store instead of calculating it
  const { isDonateNowFlow } = useFormStore();
  const steps = isDonateNowFlow
    ? [
        t("donateNow.selectCause"),
        t("StepsContainer.StepHeader.donation"),
        t("StepsContainer.StepHeader.payment"),
      ]
    : [t("StepsContainer.StepHeader.donation"), t("StepsContainer.StepHeader.payment")];

  return (
    <GenericStepHeader
      steps={steps}
      currentStep={currentStep}
      handleModalClose={handleModalClose}
      setCurrentStep={setCurrentStep}
    />
  );
}
